import { FC, ReactElement, useEffect, useState } from 'react'
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components'
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'
import ReactLoading from 'react-loading'
import { getDatabase, ref as dbref, get } from 'firebase/database'

import ExampleComponent from '../ExampleComponent'
import Rating from '../Rating'
import Bot from '../Bot'
import Videoplayer from '../Videoplayer'
import useFetch from 'react-fetch-hook'
import useGetMetadataId from 'hooks/useGetMetadataId'
import { TrackingContext } from 'contexts'

const defaultProperties: { [key: string]: string } = {
  module: 'list',
  lien_video: 'Lm4SxKfZ4sk',
  services:
    '[Prime Video]https://www.amazon.com/The-Boys-Season-3/dp/B09WV8HF7Q',
  a_revoir: 'non',
  coup_de_coeur: 'non',
  en_vedette: 'non',
  note: '3',
}

const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
  properties = defaultProperties,
  trackEvent,
}): ReactElement => {
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    colorMode === 'dark' ? colorVariables.globalColorsDark : '',
  ]

  const theme = {
    colorMode: colorMode,
    secondaryButtonBackground:
      colorMode === 'dark'
        ? 'var(--brandblue-bright-2)'
        : 'var(--beige-bright-2)',
    secondaryButtonText: colorMode === 'dark' ? '#ffffff' : '#121212',
    primaryButtonBackground: colorMode === 'dark' ? '#ffffff' : '#121212',
    primaryButtonText: colorMode === 'dark' ? '#121212' : '#ffffff',
    coloredBackground:
      colorMode === 'dark'
        ? 'var(--brandblue-bright-1)'
        : 'var(--beige-bright-3)',
    tenant: tenant ?? { key: '24heures', name: '24heures' },
    colorGreyLight: colorMode === 'dark' ? '#222222' : '#f4f4f4',
    colorGrey: colorMode === 'dark' ? '#4d4d4d' : '#e4e6e9',
    colorText: colorMode === 'dark' ? '#d2d2d2' : '#121212',
  }

  // We need this to style the rating stars
  const GlobalStyle = createGlobalStyle`
    .star-ratings .star-container svg .star {
      stroke: var(--tenant-${theme.tenant.key.slice(0, 3)});
      stroke-linejoin: round;
      stroke-width: 4px;
    }
    `

  const { isLoading, data, error } = useFetch(
    'https://storage.googleapis.com/wch-interactive/streaming-v2/list.json',
    {
      formatter: async (response: Response) => {
        const jsonData = await response.json();
  
        const convertedData = jsonData.map((item:any) => ({
          ...item,
          updatedAt: new Date(item.customUpdatedAt)
        }));
  
        return convertedData;
      },    }
  )

  const [completeData, setCompleteData] = useState<any>(data || [])

  const modules: { [key: string]: ReactElement } = {
    list: (
      <div style={{ marginBottom: 60 }}>
        <ExampleComponent text={properties?.texte} data={completeData} />
      </div>
    ),
    rating: (
      <div style={{ margin: '0 2rem 40px 2rem' }}>
        <TextWidth>
          <Rating
            authorRating={parseFloat(properties?.note)}
            id={useGetMetadataId()}
          />
        </TextWidth>
      </div>
    ),
    videoplayer: (
      <Videoplayer
        id={properties.lien_video}
        links={properties?.services?.split(',').map((l) => l.trim())}
      />
    ),
    bot: <Bot />,
  }

  // Get the public votes from the database and merge them with the data from the JSON file
  useEffect(() => {
    if (data) {
      // console.log('data', data)
      setCompleteData(data)
      const db = getDatabase()
      get(dbref(db, 'votes'))
        .then((snapshot) => {
          if (snapshot.exists()) {
            const out = data?.map((item: any, i: number) => {
              const v = snapshot.val()[item.metadataId] || null

              if (v && Object.keys(v).length > 0) {
                const c: number = Object.values(v).reduce(
                  (a: any, b: any) => a + b.votes,
                  0
                ) as number
                const s = Object.keys(v).reduce((a: any, b: any) => {
                  return a + v[b].votes * b
                }, 0)

                return Object.assign({}, item, {
                  votes: { count: c, value: s / c },
                })
              } else {
                return Object.assign({}, item, {
                  votes: { count: 0, value: 0 },
                })
              }
            })

            setCompleteData(out)
          } else {
            console.log('No data available')
          }
        })
        .catch((error) => {
          console.error(error)
        })
    }
  }, [data])

  if (error) {
    return (
      <div>
        <p>Code: ${error.status}</p>
        <p>Message: ${error.statusText}</p>
      </div>
    )
  }

  return (
    <AppContainer className={globalClasses.join(' ')}>
      <GlobalStyle />
      <ThemeProvider theme={theme}>
        <TrackingContext.Provider value={trackEvent}>
          {isLoading ? (
            <LoadingContainer>
              <ReactLoading
                type={'spin'}
                color={'var(--text-color)'}
                height={'60px'}
                width={'60px'}
              />
            </LoadingContainer>
          ) : (
            modules[properties.module]
          )}
        </TrackingContext.Provider>
      </ThemeProvider>
    </AppContainer>
  )
}
const AppContainer = styled.div`
  font-family: var(--font-noto);
  font-size: 1.8rem;
  line-height: 3rem;
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const TextWidth = styled.div`
  margin: 0 auto;
  max-width: 666px;
`
const ContentWidth = styled.div`
  margin: 0 auto;
  max-width: 1072px;
`

export default MainComponent
